(function (root) {
  function create({ locks, storage, readCart, notify = () => {} } = {}) {
    let queue = Promise.resolve(), reconciler;
    const key = 'tm-cart:pending:v2';
    const uncertain = code => Object.assign(new Error(code), { uncertain: true });
    const pending = () => {
      try {
        const raw = storage?.getItem(key);
        if (!raw) return null;
        if (raw.length > 64000) throw new Error();
        const value = JSON.parse(raw);
        if (value.version !== 2 || !value.operationId || !value.cartIdentity || !value.revision) throw new Error();
        return value;
      } catch (_) { throw uncertain('CART_RECOVERY_UNAVAILABLE'); }
    };
    const save = operation => {
      const raw = JSON.stringify(operation);
      if (!storage || raw.length > 64000) throw uncertain('CART_RECOVERY_UNAVAILABLE');
      try {
        storage.setItem(key, raw);
        if (storage.getItem(key) !== raw) throw new Error();
      } catch (_) { throw uncertain('CART_RECOVERY_UNAVAILABLE'); }
    };
    const clear = operationId => {
      if (pending()?.operationId !== operationId) throw uncertain('CART_OPERATION_CHANGED');
      try {
        storage.removeItem(key);
        if (storage.getItem(key) !== null) throw new Error();
      } catch (_) { throw uncertain('CART_RECOVERY_UNAVAILABLE'); }
    };
    const journalReady = () => {
      if (!storage) return false;
      const probe = `${key}:probe:${Math.random()}`;
      try {
        storage.setItem(probe, 'ready');
        const ready = storage.getItem(probe) === 'ready';
        storage.removeItem(probe);
        return ready;
      } catch (_) {
        try { storage.removeItem(probe); } catch (_) {}
        return false;
      }
    };
    const run = (task, { recovery = false, requireCrossTab = false } = {}) => {
      const execute = () => {
        if (requireCrossTab && !locks?.request) throw uncertain('CROSS_TAB_LOCK_UNAVAILABLE');
        let operation;
        try { operation = pending(); }
        catch (error) { if (requireCrossTab || recovery || reconciler) throw error; }
        if (!recovery && operation) throw uncertain('REVIEW_PENDING_CART_OPERATION');
        return task();
      };
      const result = queue.then(() => locks?.request ? locks.request('the-man-paid-cart', execute) : execute());
      queue = result.catch(() => {});
      return result;
    };
    const registerReconciler = handler => {
      if (reconciler || typeof handler !== 'function') throw new Error('CART_RECONCILER_ALREADY_REGISTERED');
      reconciler = handler;
      return () => { if (reconciler === handler) reconciler = undefined; };
    };
    const mutate = (task, { cartResponse = false, finalize } = {}) => run(async () => {
      const before = readCart ? await readCart() : undefined;
      let result, failure;
      try { result = await task(before); } catch (error) { failure = error; }
      let cart;
      try { if (readCart) cart = await readCart(); }
      catch (_) { throw uncertain('CART_READBACK_UNAVAILABLE'); }
      if (finalize) result = await finalize({ before, cart, result, failure });
      if (failure) throw failure;
      if (reconciler && cart && !pending()) {
        const reconciliation = await reconciler(cart);
        cart = reconciliation.cart;
        notify(reconciliation);
      }
      if (cartResponse && cart && !JSON.parse(result).status) return JSON.stringify(cart);
      if (result?.cart && cart) return { ...result, cart };
      return result;
    });
    return Object.freeze({ version: 2, run, mutate, registerReconciler, pending, save, clear, journalReady, crossTab: !!locks?.request });
  }
  if (typeof module === 'object' && module.exports) module.exports = { create };
  else if (!root.TheManCartCoordinator) {
    let storage;
    try { storage = root.localStorage; } catch (_) {}
    const readCart = async () => {
      const response = await root.fetch((root.Shopify?.routes?.root || '/') + 'cart.js', { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(15000) });
      if (!response.ok) throw new Error('CART_READ_UNAVAILABLE');
      const cart = await response.json();
      if (!Array.isArray(cart.items)) throw new Error('INVALID_CART_RESPONSE');
      return cart;
    };
    const notify = result => {
      root.document.dispatchEvent(new CustomEvent('tm:cart-reconciled', { detail: result }));
      root.document.dispatchEvent(new CustomEvent('cart:build'));
    };
    root.TheManCartCoordinator = create({ locks: root.navigator?.locks, storage, readCart, notify });
  }
})(typeof window === 'object' ? window : globalThis);
